import {
  IAcquirerOnboarding,
  IAcquirerOnboardingField,
} from '@/@types/AcquirerOnboarding';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import OnboardingSheet from '@/components/Onboarding/OnboardingSheet';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2 } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';

interface AcquirerFieldsFormProps {
  onboarding: IAcquirerOnboarding;
  isSubmitting: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => void | Promise<unknown>;
}

/**
 * Formulário dos dados que a adquirente exige antes do KYC.
 *
 * Não conhece adquirente nem campo: renderiza a descrição que o backend enviou. Adquirente nova
 * que declare outros campos aparece aqui sem tocar neste arquivo.
 */
export function AcquirerFieldsForm({
  onboarding,
  isSubmitting,
  open,
  onClose,
  onSubmit,
}: AcquirerFieldsFormProps) {
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(
      onboarding.required_fields.map((field) => [
        field.name,
        field.default ?? (field.type === 'declaration' ? false : ''),
      ])
    )
  );

  const [touched, setTouched] = useState<Record<string, boolean>>({});

  /**
   * Recusas que a adquirente devolveu na última tentativa, coladas no campo correspondente.
   * Somem assim que o vendedor altera aquele campo — erro antigo em valor novo é ruído.
   */
  const [dismissedRejections, setDismissedRejections] = useState<
    Record<string, boolean>
  >({});

  const rejections = (onboarding.field_errors ?? {}) as Record<string, string>;

  const rejectionFor = (name: string): string | null =>
    dismissedRejections[name] ? null : (rejections[name] ?? null);

  const setValue = (name: string, value: unknown) => {
    setValues((current) => ({ ...current, [name]: value }));
    setDismissedRejections((current) => ({ ...current, [name]: true }));
  };

  /** Valida contra o padrão que o backend declarou — o front não conhece formato de adquirente. */
  const fieldError = (field: IAcquirerOnboardingField): string | null => {
    const value = String(values[field.name] ?? '').trim();

    if (value === '') {
      return field.required ? 'Campo obrigatório.' : null;
    }

    if (field.pattern && !new RegExp(field.pattern).test(value)) {
      return field.placeholder
        ? `Formato inválido. Exemplo: ${field.placeholder}`
        : 'Formato inválido.';
    }

    return null;
  };

  const blockedWithoutProduct =
    onboarding.requires_product === true && onboarding.has_product !== true;

  // Campo recusado bloqueia o envio até ser alterado: reenviar o mesmo valor só gastaria uma
  // tentativa e devolveria o mesmo erro.
  const isComplete =
    !blockedWithoutProduct &&
    onboarding.required_fields.every((field) => {
    if (rejectionFor(field.name)) {
      return false;
    }

    if (field.type === 'declaration') {
      return !field.required || values[field.name] === true;
    }

    return fieldError(field) === null;
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (blockedWithoutProduct) {
      return;
    }

    onSubmit(values);
  };

  return (
    <OnboardingSheet
      open={open}
      onClose={onClose}
      isPending={isSubmitting}
      // Fechar no meio do envio deixaria o vendedor sem saber o resultado.
      blockClose={isSubmitting}
    >
      <SheetHeader className='px-6 pt-6'>
        {/* Nunca exibir o nome da adquirente ao vendedor. */}
        <SheetTitle>Conta de recebimento</SheetTitle>
      </SheetHeader>

      <form onSubmit={handleSubmit} className='flex flex-col gap-5 p-6'>
        {blockedWithoutProduct ? (
          <p className='text-sm text-red-500'>
            Crie um produto antes de enviar os dados da conta de recebimento.{' '}
            <Link
              to='/products/create-products'
              className='underline underline-offset-2'
              onClick={onClose}
            >
              Criar produto
            </Link>
          </p>
        ) : Object.keys(rejections).length > 0 ? (
          <p className='text-sm text-red-500'>
            Alguns dados não foram aceitos. Corrija os campos marcados abaixo e
            envie novamente.
          </p>
        ) : (
          <p className='text-sm text-muted-foreground'>
            Precisamos destes dados para ativar sua conta de recebimento. Depois
            de enviar, você faz a verificação e ela passa a processar suas vendas.
          </p>
        )}

        {onboarding.required_fields.map((field) => (
          <AcquirerField
            key={field.name}
            field={field}
            value={values[field.name]}
            error={
              rejectionFor(field.name) ??
              (touched[field.name] ? fieldError(field) : null)
            }
            onBlur={() => setTouched((c) => ({ ...c, [field.name]: true }))}
            onChange={(value) => setValue(field.name, value)}
          />
        ))}

        <Button type='submit' disabled={!isComplete || isSubmitting || blockedWithoutProduct}>
          {isSubmitting && <Loader2 className='h-4 w-4 animate-spin mr-2' />}
          Enviar dados
        </Button>
      </form>
    </OnboardingSheet>
  );
}

interface AcquirerFieldProps {
  field: IAcquirerOnboardingField;
  value: unknown;
  error?: string | null;
  onBlur?: () => void;
  onChange: (value: unknown) => void;
}

function AcquirerField({
  field,
  value,
  error,
  onBlur,
  onChange,
}: AcquirerFieldProps) {
  if (field.type === 'declaration') {
    return (
      <div className='flex items-start gap-2'>
        <Checkbox
          id={field.name}
          checked={value === true}
          onCheckedChange={(checked) => onChange(checked === true)}
        />
        <div className='flex flex-col'>
          <Label htmlFor={field.name} className='font-normal leading-snug'>
            {field.label}
          </Label>
          {field.help && (
            <span className='text-xs text-muted-foreground'>{field.help}</span>
          )}
        </div>
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor={field.name}>{field.label}</Label>
        <Select
          value={String(value ?? '')}
          onValueChange={(selected) => onChange(selected)}
        >
          <SelectTrigger id={field.name}>
            <SelectValue placeholder='Selecione' />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(field.options).map(([optionValue, optionLabel]) => (
              <SelectItem key={optionValue} value={optionValue}>
                {optionLabel}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {field.help && (
          <span className='text-xs text-muted-foreground'>{field.help}</span>
        )}
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-1.5 max-w-sm'>
      <Label htmlFor={field.name}>{field.label}</Label>
      <Input
        id={field.name}
        value={String(value ?? '')}
        placeholder={field.placeholder ?? undefined}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <span className='text-xs text-red-500'>{error}</span>
      ) : (
        field.help && (
          <span className='text-xs text-muted-foreground'>{field.help}</span>
        )
      )}
    </div>
  );
}
